import { DestroyRef, effect, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth0/auth0-angular';
import { TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs/operators';
import { DEFAULT_LANG } from 'src/app/app.constants';
import { CookieConsentService } from './cookie-consent.service';
import { initGoogleAnalytics } from './google-analytics';
import { CurrentSelectionsStore } from '../store/current-selections.store';
import { FeatureFlagsStore } from '../store/feature-flags.store';
import { PlaylistsStore } from '../store/playlists.store';
import { RecordersStore } from '../store/recorders.store';
import { RecordingListStore } from '../store/recording-list.store';
import { TrashStore } from '../store/trash.store';
import { UserStore } from '../store/user.store';

/* App bootstrap orchestration (formerly init.effects): locale setup and the
 * initial data loads once the user is authenticated. */
@Injectable({ providedIn: 'root' })
export class AppInitService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly auth = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly cookieConsent = inject(CookieConsentService);

  private readonly recordersStore = inject(RecordersStore);
  private readonly userStore = inject(UserStore);
  private readonly featureFlagsStore = inject(FeatureFlagsStore);
  private readonly trashStore = inject(TrashStore);
  private readonly recordingListStore = inject(RecordingListStore);
  private readonly playlistsStore = inject(PlaylistsStore);
  private readonly currentSelectionsStore = inject(CurrentSelectionsStore);

  constructor() {
    // The date locale (en) is provided app-wide via MAT_DATE_LOCALE in
    // material.providers; date-fns formatting defaults to en-US.
    this.translate.use(DEFAULT_LANG);
    this.translate.setFallbackLang(DEFAULT_LANG);

    this.auth.isAuthenticated$
      .pipe(
        filter(isAuth => isAuth === true),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.recordersStore.getRecorders();
        this.userStore.getCurrentUser();
        this.featureFlagsStore.ensureLoaded();
        this.trashStore.refresh();

        this.recordingListStore.getMetadata({ from: 0, to: 9999999999 });
        this.playlistsStore.get();
        this.currentSelectionsStore.getExportFormats();
      });

    /* Old identifyUser$: bootstrap GA with consent as soon as the user is
     * known. This waits on the loaded user rather than on isAuthenticated$
     * above, because GA wants the user id and that only arrives with the
     * profile. */
    effect(() => {
      const userId = this.userStore.userId();
      if (!userId) {
        return;
      }

      initGoogleAnalytics(userId);
      this.cookieConsent.applyStoredConsent();
    });
  }
}
